import React, { useState, useMemo } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetComprehensiveAnalyticsQuery,
  useGetAdminOrganizationsQuery,
  useGetAdminTestsQuery,
  ComprehensiveAnalytics
} from "../../app/services/meseeknaApiV2"
import { FiTrendingUp, FiUsers, FiActivity, FiAward, FiFilter, FiCalendar } from "react-icons/fi"
import { HiOutlineOfficeBuilding } from "react-icons/hi"
import { MdOutlineAnalytics, MdOutlineAssessment, MdOutlineScore, MdRefresh } from "react-icons/md"
import { BiLineChart, BiBarChart } from "react-icons/bi"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TrendingIcon = (props: { size?: number; className?: string }): JSX.Element => <FiTrendingUp {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UsersIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUsers {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ActivityIcon = (props: { size?: number; className?: string }): JSX.Element => <FiActivity {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const AwardIcon = (props: { size?: number; className?: string }): JSX.Element => <FiAward {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const FilterIcon = (props: { size?: number; className?: string }): JSX.Element => <FiFilter {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CalendarIcon = (props: { size?: number; className?: string }): JSX.Element => <FiCalendar {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const AnalyticsIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineAnalytics {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const AssessmentIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineAssessment {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ScoreIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineScore {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const RefreshIcon = (props: { size?: number; className?: string }): JSX.Element => <MdRefresh {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const LineChartIcon = (props: { size?: number; className?: string }): JSX.Element => <BiLineChart {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BarChartIcon = (props: { size?: number; className?: string }): JSX.Element => <BiBarChart {...props} />

const SimpleBarChart: React.FC<{
  data: { label: string; started: number; completed: number }[]
  height?: number
}> = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.flatMap((d) => [d.started, d.completed]), 1)

  return (
    <div className="flex items-end gap-1 justify-between" style={{ height }}>
      {data.map((item, index) => (
        <div key={index} className="flex flex-col items-center flex-1 gap-1">
          <div className="flex gap-0.5 items-end w-full justify-center" style={{ height: height - 24 }}>
            <div
              className="bg-cyan-500 rounded-t w-3 transition-all duration-500"
              style={{ height: `${(item.started / maxValue) * 100}%`, minHeight: item.started > 0 ? 4 : 0 }}
              title={`Started: ${item.started}`}
            ></div>
            <div
              className="bg-green-500 rounded-t w-3 transition-all duration-500"
              style={{ height: `${(item.completed / maxValue) * 100}%`, minHeight: item.completed > 0 ? 4 : 0 }}
              title={`Completed: ${item.completed}`}
            ></div>
          </div>
          <span className="text-xs text-gray-500 truncate max-w-full">{item.label}</span>
        </div>
      ))}
    </div>
  )
}


const ScoreDistributionChart: React.FC<{
  data: { range: string; count: number }[]
  height?: number
}> = ({ data, height = 180 }) => {
  const maxCount = Math.max(...data.map((d) => d.count), 1)

  return (
    <div className="flex items-end gap-2 justify-between" style={{ height }}>
      {data.map((item, index) => (
        <div key={index} className="flex flex-col items-center flex-1">
          <div
            className="bg-violet-500 rounded-t w-full transition-all duration-500 hover:bg-violet-600"
            style={{
              height: `${(item.count / maxCount) * 100}%`,
              minHeight: item.count > 0 ? 4 : 0,
            }}
            title={`${item.range}: ${item.count} scores`}
          />
          <span className="text-xs text-gray-500 mt-1 truncate max-w-full">{item.range}</span>
        </div>
      ))}
    </div>
  )
}

const HorizontalBarChart: React.FC<{
  data: { label: string; value: number; subLabel?: string }[]
  maxItems?: number
  color?: string
}> = ({ data, maxItems = 10, color = "bg-indigo-500" }) => {
  const displayData = data.slice(0, maxItems)
  const maxValue = Math.max(...displayData.map((d) => d.value), 1)

  return (
    <div className="space-y-2">
      {displayData.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="text-xs text-gray-600 w-32 truncate" title={item.label}>
            {item.label}
          </span>
          <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
            <div
              className={`${color} h-full rounded-full transition-all duration-500`}
              style={{ width: `${(item.value / maxValue) * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium text-gray-700 w-12 text-right">
            {item.subLabel || item.value}
          </span>
        </div>
      ))}
    </div>
  )
}

const AnalyticsPage: React.FC = () => {
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const [selectedSimId, setSelectedSimId] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<"overview" | "organizations" | "simulations" | "measures">("overview")

  const { data: orgsData } = useGetAdminOrganizationsQuery()
  const organizations = orgsData?.data || []

  const { data: testsData } = useGetAdminTestsQuery()
  const simulations = testsData?.data || []

  const { data, isLoading, refetch } = useGetComprehensiveAnalyticsQuery({
    organizationId: selectedOrgId || undefined,
    simulationId: selectedSimId || undefined,
  })

  const analytics: ComprehensiveAnalytics = data?.data || {
    summary: {
      totalSimulationsRun: 0,
      totalCompletedSimulations: 0,
      totalUsers: 0,
      totalOrganizations: 0,
      averageCompletionRate: 0,
      averageScore: null,
    },
    activityOverTime: [],
    organizationBreakdown: [],
    simulationBreakdown: [],
    scoreDistribution: [],
    measurePerformance: [],
    recentCompletions: [],
  }

  const activityChartData = useMemo(() => {
    return analytics.activityOverTime.map((d) => ({
      label: d.month,
      started: d.started,
      completed: d.completed,
    }))
  }, [analytics.activityOverTime])

  const measureData = useMemo(() => {
    return analytics.measurePerformance.map((m) => ({
      label: m.measureName,
      value: m.averageScore,
      subLabel: `${m.averageScore.toFixed(0)}%`,
    }))
  }, [analytics.measurePerformance])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <AnalyticsIcon size={24} className="text-violet-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Comprehensive Analytics
                  </h1>
                  <p className="text-gray-500">
                    All simulation data across the platform
                  </p>
                </div>
              </div>

              {/* Refresh Button */}
              <button
                onClick={() => refetch()}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
              >
                <RefreshIcon size={18} />
                Refresh Data
              </button>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <FilterIcon size={16} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Filters:</span>
                </div>

                {/* Organization Filter */}
                <div className="flex items-center gap-2">
                  <BuildingIcon size={16} className="text-gray-400" />
                  <select
                    value={selectedOrgId || ""}
                    onChange={(e) => setSelectedOrgId(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-w-[180px]"
                  >
                    <option value="">All Organizations</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.organizationName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Simulation Filter */}
                <div className="flex items-center gap-2">
                  <AssessmentIcon size={16} className="text-gray-400" />
                  <select
                    value={selectedSimId || ""}
                    onChange={(e) => setSelectedSimId(e.target.value ? parseInt(e.target.value) : null)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-w-[180px]"
                  >
                    <option value="">All Simulations</option>
                    {simulations.map((sim) => (
                      <option key={sim.id} value={sim.id}>
                        {sim.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters */}
                {(selectedOrgId || selectedSimId) && (
                  <button
                    onClick={() => {
                      setSelectedOrgId(null)
                      setSelectedSimId(null)
                    }}
                    className="text-sm text-violet-600 hover:text-violet-800"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="flex border-b border-gray-200">
                {[
                  { id: "overview", label: "Overview", icon: TrendingIcon },
                  { id: "organizations", label: "Organizations", icon: BuildingIcon },
                  { id: "simulations", label: "Simulations", icon: AssessmentIcon },
                  { id: "measures", label: "Measures", icon: ScoreIcon },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as typeof activeTab)}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? "text-violet-600 border-b-2 border-violet-600 bg-violet-50"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <tab.icon size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                      <div className="h-12 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-pulse">
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : (
              <>
                {activeTab === "overview" && (
                  <>
                    {/* Summary Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-cyan-100 rounded-lg">
                            <ActivityIcon size={20} className="text-cyan-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900">{analytics.summary.totalSimulationsRun}</p>
                            <p className="text-xs text-gray-500">Total Runs</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <AwardIcon size={20} className="text-green-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900">{analytics.summary.totalCompletedSimulations}</p>
                            <p className="text-xs text-gray-500">Completed</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-violet-100 rounded-lg">
                            <TrendingIcon size={20} className="text-violet-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900">{analytics.summary.averageCompletionRate.toFixed(0)}%</p>
                            <p className="text-xs text-gray-500">Completion Rate</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <ScoreIcon size={20} className="text-amber-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900">
                              {analytics.summary.averageScore !== null ? `${analytics.summary.averageScore.toFixed(0)}%` : "N/A"}
                            </p>
                            <p className="text-xs text-gray-500">Avg Score</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <UsersIcon size={20} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900">{analytics.summary.totalUsers}</p>
                            <p className="text-xs text-gray-500">Users</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <BuildingIcon size={20} className="text-indigo-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-gray-900">{analytics.summary.totalOrganizations}</p>
                            <p className="text-xs text-gray-500">Organizations</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* Activity Over Time Chart */}
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h2 className="font-semibold text-gray-900">Activity Over Time (Monthly)</h2>
                          <div className="flex items-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-cyan-500 rounded"></div>
                              <span className="text-gray-600">Started</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-3 bg-green-500 rounded"></div>
                              <span className="text-gray-600">Completed</span>
                            </div>
                          </div>
                        </div>
                        {activityChartData.length > 0 ? (
                          <div className="overflow-x-auto">
                            <div style={{ minWidth: Math.max(300, activityChartData.length * 50) }}>
                              <SimpleBarChart data={activityChartData} height={200} />
                            </div>
                          </div>
                        ) : (
                          <div className="h-48 flex items-center justify-center text-gray-400">
                            <LineChartIcon size={48} />
                          </div>
                        )}
                      </div>

                      {/* Score Distribution Chart */}
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h2 className="font-semibold text-gray-900 mb-4">Score Distribution</h2>
                        {analytics.scoreDistribution.length > 0 ? (
                          <ScoreDistributionChart data={analytics.scoreDistribution} height={200} />
                        ) : (
                          <div className="h-48 flex items-center justify-center text-gray-400">
                            <BarChartIcon size={48} />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Recent Completions */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <div className="flex items-center gap-2">
                          <CalendarIcon size={18} className="text-green-600" />
                          <h2 className="font-semibold text-gray-900">Recent Completions</h2>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Simulation</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {analytics.recentCompletions.slice(0, 20).map((completion) => (
                              <tr key={completion.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{completion.userName}</div>
                                    <div className="text-xs text-gray-500">{completion.userEmail}</div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {completion.organizationName || "N/A"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {completion.simulationTitle}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`text-sm font-semibold ${
                                    completion.score !== null && completion.score >= 70
                                      ? "text-green-600"
                                      : completion.score !== null && completion.score >= 50
                                      ? "text-amber-600"
                                      : "text-red-600"
                                  }`}>
                                    {completion.score !== null ? `${completion.score.toFixed(0)}%` : "N/A"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {formatDate(completion.completedAt)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {analytics.recentCompletions.length === 0 && (
                          <div className="p-8 text-center text-gray-400">
                            No recent completions
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {activeTab === "organizations" && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <BuildingIcon size={18} className="text-indigo-600" />
                        <h2 className="font-semibold text-gray-900">Organization Breakdown</h2>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Users</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Runs</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Score</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {analytics.organizationBreakdown.map((org) => (
                            <tr key={org.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {org.name}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {org.userCount}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {org.totalSimulations}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {org.completedSimulations}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-20 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-violet-500 h-2 rounded-full"
                                      style={{ width: `${org.completionRate}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-gray-600">{org.completionRate.toFixed(0)}%</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`text-sm font-semibold ${
                                  org.averageScore !== null && org.averageScore >= 70
                                    ? "text-green-600"
                                    : org.averageScore !== null && org.averageScore >= 50
                                    ? "text-amber-600"
                                    : "text-gray-400"
                                }`}>
                                  {org.averageScore !== null ? `${org.averageScore.toFixed(0)}%` : "N/A"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {analytics.organizationBreakdown.length === 0 && (
                        <div className="p-8 text-center text-gray-400">
                          No organization data available
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "simulations" && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <AssessmentIcon size={18} className="text-indigo-600" />
                        <h2 className="font-semibold text-gray-900">Simulation Breakdown</h2>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Simulation</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Runs</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organizations</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Score</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {analytics.simulationBreakdown.map((sim) => (
                            <tr key={sim.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{sim.title}</div>
                                  {sim.shortName && (
                                    <div className="text-xs text-gray-500">{sim.shortName}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {sim.totalRuns}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {sim.completedRuns}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-20 bg-gray-200 rounded-full h-2">
                                    <div
                                      className="bg-green-500 h-2 rounded-full"
                                      style={{ width: `${sim.completionRate}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-gray-600">{sim.completionRate.toFixed(0)}%</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                {sim.organizationsUsing}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`text-sm font-semibold ${
                                  sim.averageScore !== null && sim.averageScore >= 70
                                    ? "text-green-600"
                                    : sim.averageScore !== null && sim.averageScore >= 50
                                    ? "text-amber-600"
                                    : "text-gray-400"
                                }`}>
                                  {sim.averageScore !== null ? `${sim.averageScore.toFixed(0)}%` : "N/A"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {analytics.simulationBreakdown.length === 0 && (
                        <div className="p-8 text-center text-gray-400">
                          No simulation data available
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "measures" && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <ScoreIcon size={18} className="text-amber-600" />
                        <h2 className="font-semibold text-gray-900">Measure Performance</h2>
                      </div>
                    </div>
                    <div className="p-6">
                      {analytics.measurePerformance.length > 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div>
                            <h3 className="text-sm font-medium text-gray-600 mb-4">Average Scores by Measure</h3>
                            <HorizontalBarChart
                              data={measureData}
                              maxItems={15}
                              color="bg-amber-500"
                            />
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Measure</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Score</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Scores</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {analytics.measurePerformance.map((measure) => (
                                  <tr key={measure.measureId} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">{measure.measureName}</div>
                                        {measure.measureCode && (
                                          <div className="text-xs text-gray-500">{measure.measureCode}</div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <span className={`text-sm font-semibold ${
                                        measure.averageScore >= 70
                                          ? "text-green-600"
                                          : measure.averageScore >= 50
                                          ? "text-amber-600"
                                          : "text-red-600"
                                      }`}>
                                        {measure.averageScore.toFixed(1)}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                      {measure.totalScores}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="p-8 text-center text-gray-400">
                          No measure performance data available
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default AnalyticsPage
